import { BEIGE_300, MOBILE_VIEW_WIDTH_QUERY, RhetorikFontFamily } from 'constants/styling/theme'
import { FC, ReactNode } from 'react'
import { Stack, Typography, useMediaQuery } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import Button from 'components/common/Button/Button'
import { Link } from 'react-router-dom'
import { MUIButton } from 'components/common/MUIButton'
import { PageTransition } from 'utils/animations'

export interface StatusPageButton {
  text: string
  onClick: () => void
  variant?: 'primary' | 'secondaryBorder'
}

export interface StatusPageLinkButton {
  text: string
  to: string
  variant?: 'link' | 'link orange'
}

export interface StatusPageProps {
  title?: string
  titleTrans?: {
    i18nKey: string
    components?: React.ReactElement[]
  }
  description?: string
  descriptionTrans?: {
    i18nKey: string
    components?: React.ReactElement[]
  }
  primaryButton: StatusPageButton
  secondaryButton?: StatusPageButton
  linkButton?: StatusPageLinkButton
  children?: ReactNode
}

/**
 * @component
 * A reusable status page component for displaying titles, descriptions, and action buttons.
 * Used for pages like SignupPortal, NotFound, 404, 401, etc.
 * 
 * @example
 * <StatusPage
 *   title="Sign Up"
 *   description="You are currently authenticated as John Doe"
 *   primaryButton={{
 *     text: "Continue to Dashboard",
 *     onClick: () => navigate('/dashboard')
 *   }}
 *   secondaryButton={{
 *     text: "Log Out",
 *     onClick: handleLogout
 *   }}
 * />
 * 
 * @example
 * <StatusPage
 *   title="Error"
 *   descriptionTrans={{
 *     i18nKey: "error_with_html",
 *     components: [<strong />, <br />]
 *   }}
 *   primaryButton={{
 *     text: "Go Home",
 *     onClick: () => navigate('/')
 *   }}
 * />
 */
export const StatusPage: FC<StatusPageProps> = ({
  title,
  titleTrans,
  description,
  descriptionTrans,
  primaryButton,
  secondaryButton,
  linkButton,
  children
}) => {
  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)
  const { t } = useTranslation()

  return (
    <PageTransition>
      <Stack
        alignItems="center"
        justifyContent="center"
        position="relative"
        bgcolor={BEIGE_300}
        padding={isMobileView ? '2rem' : '0'}
        height={isMobileView ? '100%' : '100vh'}
      >
        <Stack direction="column" maxWidth="58rem" justifyContent="center" alignItems="center" textAlign="center">

          {titleTrans ? (
            <Typography variant="display-md" fontWeight={300} fontFamily={RhetorikFontFamily}>
              <Trans
                t={t}
                i18nKey={titleTrans.i18nKey}
                components={titleTrans.components}
              />
            </Typography>
          ) : title && (
            <Typography variant="display-md" fontWeight={300} fontFamily={RhetorikFontFamily}>{title}</Typography>
          )}

          {descriptionTrans ? (
            <Typography variant="text-md" marginTop="3.6rem">
              <Trans
                t={t}
                i18nKey={descriptionTrans.i18nKey}
                components={descriptionTrans.components}
              />
            </Typography>
          ) : description && (
            <Typography variant="text-md" marginTop="3.6rem">{description}</Typography>
          )}

          {children}

          <Stack direction="row" gap="2.4rem" marginTop="3.6em">
            {secondaryButton && (
              <MUIButton
                size="lg"
                type="secondaryBorder"
                onClick={secondaryButton.onClick}
              >
                <Typography variant="text-md" fontWeight={450}>
                  {secondaryButton.text}
                </Typography>
              </MUIButton>
            )}

            <MUIButton
              size="lg"
              type="defaultPrimary"
              onClick={primaryButton.onClick}
            >
              <Typography variant="text-md" fontWeight={450}>
                {primaryButton.text}
              </Typography>
            </MUIButton>
          </Stack>

          {linkButton && (
            <Stack marginTop="2.4rem" alignItems="center">
              <Link to={linkButton.to}>
                <Button type={linkButton.variant || 'link orange'}>
                  {linkButton.text}
                </Button>
              </Link>
            </Stack>
          )}
        </Stack>
      </Stack>
    </PageTransition>
  )
}
